import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, ShieldCheck, CaretUp, CaretDown, CaretUpDown, ArrowsClockwise, Globe, DownloadSimple } from "@phosphor-icons/react";
import { api, formatApiError, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { DomainDetailModal } from "@/components/DomainDetailModal";
import { BulkActionsModal } from "@/components/BulkActionsModal";

function daysUntil(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.round(ms / 86400000);
}

function ExpiryPill({ iso, label = "domain" }) {
    if (!iso) return <span className="text-[color:var(--dm-text-2)] text-xs">—</span>;
    const d = daysUntil(iso);
    const cls = d < 0 ? "pill-danger" : d < 30 ? "pill-warn" : d < 90 ? "pill-neutral" : "pill-ok";
    return (
        <span className={`pill ${cls}`} title={new Date(iso).toLocaleDateString()}>
            {d < 0 ? `expired ${-d}d` : `${d}d`}
        </span>
    );
}

function SortHeader({ field, label, align = "left", sortField, sortDir, onSort }) {
    const active = sortField === field;
    return (
        <button
            type="button"
            onClick={() => onSort(field)}
            data-testid={`sort-${field}`}
            className={`flex items-center gap-1.5 uppercase tracking-wider text-[0.7rem] font-bold text-[color:var(--dm-text-2)] hover:text-[color:var(--dm-brand)] transition-colors ${
                align === "right" ? "justify-end w-full" : ""
            }`}
        >
            <span>{label}</span>
            {active ? (
                sortDir === "asc" ? (
                    <CaretUp size={12} weight="bold" className="text-[color:var(--dm-brand)]" />
                ) : (
                    <CaretDown size={12} weight="bold" className="text-[color:var(--dm-brand)]" />
                )
            ) : (
                <CaretUpDown size={12} weight="regular" className="opacity-40" />
            )}
        </button>
    );
}

export default function DomainsPage() {
    const [params, setParams] = useSearchParams();
    const initialQ = params.get("q") || "";
    const initialRegistrar = params.get("registrar") || "";
    const initialAccount = params.get("account") || "";
    const initialExpiring = params.get("expiring_days") || "";
    const initialSslExpiring = params.get("ssl_expiring_days") || "";
    const initialSort = params.get("sort") || "expires_at";
    const primarySortToken = initialSort.split(",")[0].trim();
    const initialSortField = primarySortToken.replace(/^-/, "") || "expires_at";
    const initialSortDir = primarySortToken.startsWith("-") ? "desc" : "asc";

    const [q, setQ] = useState(initialQ);
    const [registrar, setRegistrar] = useState(initialRegistrar);
    const [account, setAccount] = useState(initialAccount);
    const [expiring, setExpiring] = useState(initialExpiring);
    const [sslExpiring, setSslExpiring] = useState(initialSslExpiring);
    // For multi-field sort passed via URL (e.g. account,expires_at) we keep the *original*
    // sort string as `sortChain`; when the user clicks a header we drop the chain and use
    // the single-field sort. This preserves the "group by account" behaviour from the
    // dashboard deep-link while still allowing per-column overrides.
    const [sortChain, setSortChain] = useState(initialSort.includes(",") ? initialSort : null);
    const [sortField, setSortField] = useState(initialSortField);
    const [sortDir, setSortDir] = useState(initialSortDir);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [knownAccounts, setKnownAccounts] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkAction, setBulkAction] = useState(null); // "renew" | "nameservers" | null
    const limit = 100;

    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleSelectAllOnPage = (checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) rows.forEach((r) => next.add(r.id));
            else rows.forEach((r) => next.delete(r.id));
            return next;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());
    const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

    const sortParam = useMemo(() => {
        if (sortChain) return sortChain;
        return sortDir === "desc" ? `-${sortField}` : sortField;
    }, [sortChain, sortField, sortDir]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/domains", {
                params: {
                    q: q || undefined,
                    registrar: registrar || undefined,
                    account: account || undefined,
                    expiring_days: expiring ? Number(expiring) : undefined,
                    sort: sortParam,
                    limit,
                    offset,
                },
            });
            setRows(data.items);
            setTotal(data.total);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registrar, account, expiring, offset, sortField, sortDir]);

    useEffect(() => {
        const t = setTimeout(() => {
            setOffset(0);
            load();
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    useEffect(() => {
        // fetch the list of known account names (once) for the datalist autocomplete
        api.get("/dashboard/stats")
            .then(({ data }) => {
                const names = Array.from(
                    new Set((data.per_account || []).map((p) => p.account))
                ).sort();
                setKnownAccounts(names);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const nextParams = {};
        if (q) nextParams.q = q;
        if (registrar) nextParams.registrar = registrar;
        if (account) nextParams.account = account;
        if (expiring) nextParams.expiring_days = expiring;
        if (sslExpiring) nextParams.ssl_expiring_days = sslExpiring;
        if (sortParam && sortParam !== "expires_at") nextParams.sort = sortParam;
        setParams(nextParams, { replace: true });
    }, [q, registrar, account, expiring, sslExpiring, sortParam, setParams]);

    const onSort = (field) => {
        // Any manual column click drops the deep-link multi-field chain
        setSortChain(null);
        if (sortField === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("asc");
        }
        setOffset(0);
    };

    const probeOne = async (id) => {
        try {
            const { data } = await api.post(`/domains/${id}/probe-ssl`);
            if (data.ok) toast.success("SSL probed");
            else toast.error(data.result?.error || "probe failed");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const probeBatch = async () => {
        try {
            const { data } = await api.post("/domains/probe-ssl-batch", null, {
                params: { limit: 100, only_missing: true },
            });
            toast.success(`Probing ${data.scheduled} domains in background`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const downloadCsv = async () => {
        toast.info("Preparing CSV — this may take a few seconds for large exports…");
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (registrar) params.set("registrar", registrar);
            if (account) params.set("account", account);
            if (expiring) params.set("expiring_days", expiring);
            if (sslExpiring) params.set("ssl_expiring_days", sslExpiring);
            if (sortParam) params.set("sort", sortParam);
            const url = `${API_BASE}/domains/export.csv?${params.toString()}`;
            const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const a = document.createElement("a");
            const objUrl = URL.createObjectURL(blob);
            a.href = objUrl;
            a.download = `domains-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objUrl);
            toast.success("CSV downloaded");
        } catch (e) {
            toast.error(e.message || "Download failed");
        }
    };

    return (
        <div className="space-y-4" data-testid="domains-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1
                        className="text-3xl font-extrabold"
                        style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                    >
                        Domains
                    </h1>
                    <p className="text-sm text-[color:var(--dm-text-2)] mt-1">
                        {total.toLocaleString()} total across every connected account
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={downloadCsv}
                        className="btn-ghost text-sm"
                        data-testid="download-csv-button"
                        title="Download filtered domain list as CSV (opens in Excel)"
                    >
                        <DownloadSimple size={16} weight="duotone" /> Download CSV
                    </button>
                    <button
                        onClick={probeBatch}
                        className="btn-ghost text-sm"
                        data-testid="probe-ssl-batch-button"
                    >
                        <ShieldCheck size={16} weight="duotone" /> Probe SSL (batch)
                    </button>
                </div>
            </div>

            <div className="glass p-4 flex flex-wrap gap-3 items-center">
                <div className="glass-input flex items-center gap-2 !py-2 !max-w-md">
                    <MagnifyingGlass size={16} className="text-[color:var(--dm-text-2)]" />
                    <input
                        data-testid="domains-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search domain…"
                        className="bg-transparent outline-none text-sm w-full"
                    />
                </div>
                <select
                    data-testid="filter-registrar"
                    value={registrar}
                    onChange={(e) => {
                        setRegistrar(e.target.value);
                        setOffset(0);
                    }}
                    className="glass-input !py-2 !w-auto"
                >
                    <option value="">All registrars</option>
                    <option value="godaddy">GoDaddy</option>
                    <option value="namecheap">Namecheap</option>
                </select>
                <input
                    data-testid="filter-account"
                    value={account}
                    onChange={(e) => {
                        setAccount(e.target.value);
                        setOffset(0);
                    }}
                    placeholder="Account (type or pick)"
                    list="known-accounts"
                    className="glass-input !py-2 !w-56"
                />
                <datalist id="known-accounts">
                    {knownAccounts.map((n) => (
                        <option key={n} value={n} />
                    ))}
                </datalist>
                <select
                    data-testid="filter-expiring"
                    value={expiring}
                    onChange={(e) => {
                        setExpiring(e.target.value);
                        setOffset(0);
                    }}
                    className="glass-input !py-2 !w-auto"
                >
                    <option value="">Any expiry</option>
                    <option value="30">≤ 30 days</option>
                    <option value="60">≤ 60 days</option>
                    <option value="90">≤ 90 days</option>
                </select>
            </div>

            <div className="glass overflow-hidden">
                <div
                    className="table-row sticky top-0 z-10 bg-white/85 backdrop-blur-md !border-b !border-[rgba(15,41,89,0.09)]"
                    style={{ gridTemplateColumns: "40px 2.2fr 1fr 1.4fr 0.9fr 0.9fr 0.9fr" }}
                >
                    <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                        data-testid="select-all-checkbox"
                        className="cursor-pointer"
                    />
                    <SortHeader field="domain_name" label="Domain" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                    <SortHeader field="registrar" label="Registrar" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                    <SortHeader field="registrar_account_name" label="Account" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                    <SortHeader field="expires_at" label="Renewal" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                    <SortHeader field="ssl_expires_at" label="SSL cert" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                    <div className="text-right text-[0.7rem] uppercase tracking-wider text-[color:var(--dm-text-2)] font-bold">
                        Actions
                    </div>
                </div>
                <div className="max-h-[65vh] overflow-y-auto">
                {loading && (
                    <div className="p-6 text-sm text-[color:var(--dm-text-2)]">Loading…</div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="p-8 text-sm text-[color:var(--dm-text-2)] text-center">
                        No domains match your filters. Connect a registrar account and sync to populate this list.
                    </div>
                )}
                {rows.map((r) => (
                    <div
                        key={r.id}
                        className="table-row cursor-pointer"
                        data-testid={`domain-row-${r.domain_name}`}
                        onClick={() => setSelectedDomain(r)}
                        style={{ gridTemplateColumns: "40px 2.2fr 1fr 1.4fr 0.9fr 0.9fr 0.9fr" }}
                    >
                        <input
                            type="checkbox"
                            checked={selectedIds.has(r.id)}
                            onChange={() => toggleSelected(r.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`select-${r.domain_name}`}
                            className="cursor-pointer"
                        />
                        <div className="mono text-sm font-medium">{r.domain_name}</div>
                        <div>
                            <span
                                className={`pill ${
                                    r.registrar === "godaddy" ? "pill-warn" : "pill-brand"
                                }`}
                            >
                                {r.registrar}
                            </span>
                        </div>
                        <div className="mono text-xs text-[color:var(--dm-text-2)]">
                            {r.registrar_account_name}
                        </div>
                        <div>
                            <ExpiryPill iso={r.expires_at} />
                        </div>
                        <div>
                            {r.ssl_expires_at ? (
                                <ExpiryPill iso={r.ssl_expires_at} />
                            ) : r.ssl_error ? (
                                <span className="pill pill-danger" title={r.ssl_error}>
                                    err
                                </span>
                            ) : (
                                <span className="text-[color:var(--dm-text-2)] text-xs">—</span>
                            )}
                        </div>
                        <div className="text-right flex items-center justify-end gap-3">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDomain(r);
                                }}
                                className="text-xs text-[color:var(--dm-brand)] font-semibold hover:underline"
                                data-testid={`edit-domain-${r.domain_name}`}
                            >
                                Edit
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    probeOne(r.id);
                                }}
                                className="text-xs text-[color:var(--dm-text-2)] font-semibold hover:underline"
                                data-testid={`probe-ssl-${r.domain_name}`}
                            >
                                Probe SSL
                            </button>
                        </div>
                    </div>
                ))}
                </div>
            </div>

            {total > limit && (
                <div className="flex items-center justify-between text-sm">
                    <button
                        className="btn-ghost"
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                        data-testid="pagination-prev"
                    >
                        ← Previous
                    </button>
                    <div className="text-[color:var(--dm-text-2)]">
                        {offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}
                    </div>
                    <button
                        className="btn-ghost"
                        disabled={offset + limit >= total}
                        onClick={() => setOffset(offset + limit)}
                        data-testid="pagination-next"
                    >
                        Next →
                    </button>
                </div>
            )}

            <DomainDetailModal
                domain={selectedDomain}
                onClose={() => setSelectedDomain(null)}
                onChanged={load}
            />

            <BulkActionsModal
                open={bulkAction !== null}
                action={bulkAction}
                selectedIds={Array.from(selectedIds)}
                onClose={() => setBulkAction(null)}
                onDone={() => {
                    clearSelection();
                    load();
                }}
            />

            {selectedIds.size > 0 && (
                <div
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 glass-strong px-5 py-3 flex items-center gap-4 shadow-2xl"
                    data-testid="bulk-action-bar"
                    style={{ borderRadius: "999px" }}
                >
                    <span className="text-sm font-semibold">
                        <span className="text-[color:var(--dm-brand)]">{selectedIds.size}</span> selected
                    </span>
                    <button
                        onClick={() => setBulkAction("renew")}
                        disabled={selectedIds.size > 50}
                        className="btn-glossy !py-1.5 !px-4 text-sm"
                        data-testid="bulk-renew-button"
                        title={selectedIds.size > 50 ? "Max 50 per batch" : "Renew all selected"}
                    >
                        <ArrowsClockwise size={14} weight="bold" /> Renew
                    </button>
                    <button
                        onClick={() => setBulkAction("nameservers")}
                        disabled={selectedIds.size > 50}
                        className="btn-glossy !py-1.5 !px-4 text-sm"
                        data-testid="bulk-ns-button"
                    >
                        <Globe size={14} weight="bold" /> Set NS
                    </button>
                    <button
                        onClick={clearSelection}
                        className="text-xs text-[color:var(--dm-text-2)] hover:text-[color:var(--dm-danger)]"
                        data-testid="bulk-clear-button"
                    >
                        Clear
                    </button>
                    {selectedIds.size > 50 && (
                        <span className="pill pill-warn">Max 50 per batch</span>
                    )}
                </div>
            )}
        </div>
    );
}
